import React, { useEffect, useState } from "react";
import { Share, X, House } from "@phosphor-icons/react";

/**
 * iOS-Safari-only banner explaining how to add MICM to the Home Screen —
 * iOS doesn't fire the beforeinstallprompt event, so admins have to
 * discover the Share → "Add to Home Screen" flow themselves.
 *
 * Only shows if:
 *  1) The user is on iOS Safari (not Chrome/Firefox on iOS)
 *  2) The app is NOT already running in standalone mode (i.e. not installed)
 *  3) The user hasn't dismissed the banner before
 */
const DISMISS_KEY = "micm-ios-install-hint-dismissed";

export default function IosInstallHint() {
    const [show, setShow] = useState(false);

    useEffect(() => {
        try {
            if (localStorage.getItem(DISMISS_KEY)) return;
            const ua = window.navigator.userAgent || "";
            const isIos = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
            // Safari on iOS reports "Safari" in UA and NOT "CriOS" (Chrome iOS) or "FxiOS" (Firefox iOS).
            const isSafari = isIos && /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
            const standalone = ("standalone" in window.navigator && window.navigator.standalone)
                            || window.matchMedia("(display-mode: standalone)").matches;
            if (isSafari && !standalone) {
                // Small delay so it doesn't fight the auth screen
                setTimeout(() => setShow(true), 1500);
            }
        } catch { /* ignore */ }
    }, []);

    if (!show) return null;
    const dismiss = () => {
        try { localStorage.setItem(DISMISS_KEY, "1"); } catch {}
        setShow(false);
    };

    return (
        <div
            className="fixed bottom-4 left-1/2 z-[60] w-[92%] max-w-md -translate-x-1/2 rounded-2xl border border-slate-200 bg-white p-4 shadow-2xl"
            data-testid="ios-install-hint"
        >
            <button
                type="button"
                onClick={dismiss}
                className="absolute right-2 top-2 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                data-testid="ios-install-dismiss"
                aria-label="Dismiss"
            >
                <X size={14} />
            </button>
            <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
                    <House size={20} weight="fill" />
                </div>
                <div className="min-w-0 flex-1 pr-4">
                    <div className="font-display text-sm font-semibold text-slate-900">Install MICM on your iPhone</div>
                    <div className="mt-1 text-[12px] leading-snug text-slate-600">
                        Tap the <Share size={13} className="inline align-middle text-blue-600" weight="fill" /> <span className="font-semibold">Share</span> button in Safari, then choose <span className="font-semibold">"Add to Home Screen"</span> to open MICM like a real app — full-screen, faster, and it works offline for viewing.
                    </div>
                </div>
            </div>
        </div>
    );
}
